
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { useOptimizedAuth } from "@/hooks/useOptimizedAuth";
import { motion, AnimatePresence } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";

import ModernProfileHeader from "@/components/settings/ModernProfileHeader";
import SecuritySettings from "@/components/settings/SecuritySettings";
import UserPrivacySettings from "@/components/settings/UserPrivacySettings";
import UserAppearanceSettings from "@/components/settings/UserAppearanceSettings";
import EnhancedAppearanceSettings from "@/components/settings/EnhancedAppearanceSettings";
import AIPersonalizationSettings from "@/components/settings/AIPersonalizationSettings";
import DeviceManagementSettings from "@/components/settings/DeviceManagementSettings";
import NotificationPreferences from "@/components/settings/NotificationPreferences";
import DataManagement from "@/components/settings/DataManagement";
import SettingsLayout from "@/components/settings/SettingsLayout";
import { useSimpleProfile } from "@/hooks/useSimpleProfile";
import ProfileSettingsLoading from "@/components/dashboard/ProfileSettingsLoading";
import ModernSettingsSidebar from "@/components/settings/ModernSettingsSidebar";
import ProfileSettings from "@/components/settings/ProfileSettings";
import OrderHistoryTab from "@/components/settings/OrderHistoryTab";
import AddressSettings from "@/components/settings/AddressSettings";
import PaymentSettings from "@/components/settings/PaymentSettings";
import WishlistSettingsTab from "@/components/settings/WishlistSettingsTab";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Settings as SettingsIcon, Shield, Activity, Zap } from "lucide-react";
import BackButton from "@/components/navigation/BackButton";
import { MobileSettingsHeader } from "@/components/settings/MobileSettingsHeader";
import { MobileSettingsNavigation } from "@/components/settings/MobileSettingsNavigation";
import MobileSettingsCard from "@/components/settings/MobileSettingsCard";
import {
  User, MapPin, Package, Heart, CreditCard, Smartphone,
  Lock, Palette, Sparkles, Bell, Mail, ShoppingBag, Accessibility,
  Database
} from "lucide-react";

const settingsComponents = {
    profile: {
        component: <ProfileSettings />,
        title: "Profile",
        description: "Profile picture, name, email, phone, and personal details"
    },
    addresses: {
        component: <AddressSettings />,
        title: "Addresses",
        description: "Manage your shipping and billing addresses"
    },
    orders: {
        component: <OrderHistoryTab />,
        title: "Orders & Returns",
        description: "View order history, track packages, and manage returns"
    },
    wishlist: {
        component: <WishlistSettingsTab />,
        title: "My Wishlist",
        description: "Manage your saved items and favorites"
    },
    payments: {
        component: <PaymentSettings />,
        title: "Payment Methods",
        description: "Manage your cards and payment preferences"
    },
    security: {
        component: <SecuritySettings />,
        title: "Account & Security",
        description: "Password, two-factor authentication, and login activity"
    },
    devices: {
        component: <DeviceManagementSettings />,
        title: "Device Management",
        description: "Manage active sessions and connected devices"
    },
    appearance: {
        component: <EnhancedAppearanceSettings />,
        title: "Appearance & Theme",
        description: "Customize colors, theme, and visual preferences"
    },
    "ai-personalization": {
        component: <AIPersonalizationSettings />,
        title: "AI Personalization",
        description: "Control smart recommendations and AI-powered features"
    },
    notifications: {
        component: <NotificationPreferences />,
        title: "Notifications",
        description: "Email, SMS, and push notification preferences"
    },
    communication: {
        component: <div>Communication Settings coming soon...</div>,
        title: "Communication Preferences",
        description: "Manage email frequency and contact preferences"
    },
    shopping: {
        component: <div>Shopping Preferences coming soon...</div>,
        title: "Shopping Preferences",
        description: "Default preferences and commerce settings"
    },
    accessibility: {
        component: <div>Accessibility Settings coming soon...</div>,
        title: "Accessibility",
        description: "Inclusive design and accessibility features"
    },
    automation: {
        component: <div>Automation coming soon...</div>,
        title: "Automation & Workflows",
        description: "Smart rules and automated actions"
    },
    privacy: {
        component: <UserPrivacySettings />,
        title: "Privacy & Preferences",
        description: "Data privacy, cookie preferences, and account settings"
    },
    data: {
        component: <DataManagement />,
        title: "Data Management",
        description: "Download your data or permanently delete your account"
    },
};

const Settings = () => {
    const { user, loading } = useOptimizedAuth();
    const navigate = useNavigate();
    const { section } = useParams();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const isMobile = useIsMobile();

    // Mobile settings groups
    const mobileSettingsGroups = [
        {
            title: 'Account',
            icon: User,
            items: [
                { name: 'Profile', tab: 'profile', icon: User, description: 'Personal information' },
                { name: 'Addresses', tab: 'addresses', icon: MapPin, description: 'Delivery & billing' },
                { name: 'Orders', tab: 'orders', icon: Package, description: 'Order history', badge: 12 },
                { name: 'Wishlist', tab: 'wishlist', icon: Heart, description: 'Saved items', badge: 5 },
                { name: 'Payments', tab: 'payments', icon: CreditCard, description: 'Payment methods' },
            ]
        },
        {
            title: 'Security & Privacy',
            icon: Shield,
            items: [
                { name: 'Security', tab: 'security', icon: Shield, description: '2FA & password' },
                { name: 'Devices', tab: 'devices', icon: Smartphone, description: 'Active sessions' },
                { name: 'Privacy', tab: 'privacy', icon: Lock, description: 'Data & visibility' },
            ]
        },
        {
            title: 'Preferences',
            icon: SettingsIcon,
            items: [
                { name: 'Appearance', tab: 'appearance', icon: Palette, description: 'Theme & layout' },
                { name: 'AI Assistant', tab: 'ai-personalization', icon: Sparkles, description: 'Smart features', isBeta: true },
                { name: 'Notifications', tab: 'notifications', icon: Bell, description: 'Alerts & updates' },
                { name: 'Communication', tab: 'communication', icon: Mail, description: 'Email preferences' },
                { name: 'Shopping', tab: 'shopping', icon: ShoppingBag, description: 'Default settings' },
                { name: 'Accessibility', tab: 'accessibility', icon: Accessibility, description: 'Enhanced UX' },
            ]
        },
        {
            title: 'Data & Account',
            icon: Database,
            items: [
                { name: 'Automation', tab: 'automation', icon: Zap, description: 'Smart workflows' },
                { name: 'Data Management', tab: 'data', icon: Database, description: 'Export & delete' },
            ]
        }
    ];
  
    useEffect(() => {
      if (!loading && !user) {
        navigate("/auth");
      }
    }, [user, loading, navigate]);

    const { profile, isLoading } = useSimpleProfile();
    const [searchParams] = useSearchParams();
    const activeTab = section || searchParams.get('tab') || 'profile';

    if (loading || isLoading) {
      return <ProfileSettingsLoading />;
    }

    if (!user) {
        return null;
    }
    
    const activeSettings = settingsComponents[activeTab as keyof typeof settingsComponents] || settingsComponents.profile;

    // Calculate profile completion
    const profileFields = [
        profile?.full_name,
        profile?.phone,
        profile?.bio,
        profile?.avatar_url,
        profile?.date_of_birth
    ];
    const completedFields = profileFields.filter(Boolean).length;
    const profileCompletion = Math.round((completedFields / profileFields.length) * 100);

    return (
        <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30">
            {isMobile ? (
                /* Mobile Layout */
                <>
                    <MobileSettingsHeader profile={profile} user={user} />
                    
                    <div className="px-4 pt-4 pb-24">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeTab}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.2 }}
                            >
                                {/* Show grouped cards on "profile" tab, otherwise show specific setting */}
                                {activeTab === 'profile' ? (
                                    <MobileSettingsCard 
                                        groups={mobileSettingsGroups} 
                                        activeTab={activeTab} 
                                    />
                                ) : (
                                    <SettingsLayout
                                        title={activeSettings.title}
                                        description={activeSettings.description}
                                    >
                                        {activeSettings.component}
                                    </SettingsLayout>
                                )}
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    <MobileSettingsNavigation orderCount={12} />
                </>
            ) : (
                /* Desktop Layout */
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="w-full px-4 md:px-8 lg:px-12"
                >
                    <div className="pt-6">
                        <BackButton to="/profile" label="Back to Profile" />
                    </div>
                    {/* Enhanced Profile Header */}
                    <ModernProfileHeader profile={profile} user={user} />
                    
                    {/* Main Content Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mt-8">
                        {/* Sidebar */}
                        <aside className="lg:col-span-1">
                            <div className="sticky top-6">
                                <ModernSettingsSidebar activeTab={activeTab} />
                                
                                {/* Profile Completion Card */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.2 }}
                                    className="mt-6"
                                >
                                    <Card className="bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20 backdrop-blur-sm">
                                        <div className="p-6 space-y-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                                    <Activity className="h-5 w-5 text-primary" />
                                                </div>
                                                <div>
                                                    <h3 className="font-semibold text-sm">Profile Status</h3>
                                                    <p className="text-xs text-muted-foreground">Complete your profile</p>
                                                </div>
                                            </div>
                                            
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm font-medium">{profileCompletion}% Complete</span>
                                                    <Badge variant="secondary" className="text-xs">
                                                        {profileCompletion === 100 ? 'Complete' : 'In Progress'}
                                                    </Badge>
                                                </div>
                                                <Progress value={profileCompletion} className="h-2" />
                                            </div>
                                            
                                            {profileCompletion < 100 && (
                                                <div className="space-y-2">
                                                    <p className="text-xs text-muted-foreground">Missing:</p>
                                                    <div className="space-y-1">
                                                        {!profile?.full_name && <div className="text-xs text-muted-foreground">• Full name</div>}
                                                        {!profile?.phone && <div className="text-xs text-muted-foreground">• Phone number</div>}
                                                        {!profile?.bio && <div className="text-xs text-muted-foreground">• Bio</div>}
                                                        {!profile?.avatar_url && <div className="text-xs text-muted-foreground">• Profile picture</div>}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </Card>
                                </motion.div>

                                {/* Security Status */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.3 }}
                                    className="mt-6"
                                >
                                    <Card className="bg-gradient-to-br from-green-500/5 to-emerald-500/5 border-green-500/20 backdrop-blur-sm">
                                        <div className="p-6 space-y-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                                                    <Shield className="h-5 w-5 text-green-600" />
                                                </div>
                                                <div>
                                                    <h3 className="font-semibold text-sm">Security</h3>
                                                    <p className="text-xs text-muted-foreground">Account protected</p>
                                                </div>
                                            </div>
                                            
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm text-green-600 font-medium">Secure</span>
                                                    <Badge className="text-xs bg-green-100 text-green-800">
                                                        <Zap className="h-3 w-3 mr-1" />
                                                        Active
                                                    </Badge>
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    Last security check: Today
                                                </div>
                                            </div>
                                        </div>
                                    </Card>
                                </motion.div>
                            </div>
                        </aside>

                        {/* Main Content */}
                        <main className="lg:col-span-4">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={activeTab}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.2 }}
                                    className="w-full"
                                >
                                    <SettingsLayout
                                        title={activeSettings.title}
                                        description={activeSettings.description}
                                    >
                                        {activeSettings.component}
                                    </SettingsLayout>
                                </motion.div>
                            </AnimatePresence>
                        </main>
                    </div>
                </motion.div>
            )}
        </div>
    );
};

export default Settings;
